import { useState, useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { GlofoxDocSearch } from "@/components/portal/GlofoxDocSearch";
import { GlofoxSidebarNav } from "@/components/portal/GlofoxSidebarNav";
import {
  MOBILE_NAV_DRAWER_ID,
  MobileNavDrawer,
} from "@/components/portal/MobileNavDrawer";
import { SkipToContent } from "@/components/portal/SkipToContent";
import { UserAccountMenu } from "@/components/portal/UserAccountMenu";
import {
  getAuthSessionSnapshot,
  getContactFirstName,
  subscribeAuthSession,
} from "@/lib/auth/session";
import {
  getGlofoxApiReferenceHref,
  GLOFOX_REQUEST_ACCESS_HREF,
  resolveGlofoxTopNavActive,
} from "@/lib/portal/glofox-routes";

export function GlofoxNav() {
  const { pathname } = useLocation();
  const [menuState, setMenuState] = useState({ pathname, open: false });
  if (menuState.pathname !== pathname) {
    setMenuState({ pathname, open: false });
  }
  const menuOpen = menuState.open;
  const session = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const displayName = session ? getContactFirstName(session.user.contact_name) : null;
  const { docs: docsActive, apiReference: apiReferenceActive, requestAccess: requestAccessActive } =
    resolveGlofoxTopNavActive(pathname);

  const navLinkColor = (active: boolean) =>
    active ? "var(--gf)" : "rgba(255,255,255,.6)";

  function closeMenu() {
    setMenuState((prev) => (prev.open ? { ...prev, open: false } : prev));
  }

  function toggleMenu() {
    setMenuState((prev) => ({ ...prev, open: !prev.open }));
  }

  const authBlock = displayName ? (
    <UserAccountMenu
      userName={displayName}
      testIdPrefix="gf-nav"
      variant="portal"
    />
  ) : (
    <>
      <Link
        to="/login"
        className="btn btn-outline portal-topnav__auth-btn"
        data-testid="gf-nav-login"
        onClick={closeMenu}
      >
        <i className="ti ti-login" aria-hidden />
        <span className="portal-topnav__auth-label">Log in</span>
      </Link>
      <Link
        to="/signup"
        className="btn btn-outline portal-topnav__auth-btn"
        data-testid="gf-nav-signup"
        onClick={closeMenu}
      >
        <i className="ti ti-user-plus" aria-hidden />
        <span className="portal-topnav__auth-label">Sign up</span>
      </Link>
    </>
  );

  return (
    <>
      <SkipToContent />
      <nav
      className="topnav portal-topnav"
      data-testid="gf-nav"
      style={{ background: "#0d1e1a", borderBottom: "2px solid var(--gf)" }}
      aria-label="Glofox developer portal"
    >
      <div className="portal-topnav__left">
        <button
          type="button"
          className="portal-topnav__menu-toggle"
          data-testid="gf-nav-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls={MOBILE_NAV_DRAWER_ID}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={toggleMenu}
        >
          <i className={`ti ${menuOpen ? "ti-x" : "ti-menu-2"}`} aria-hidden />
        </button>

        <div className="portal-topnav__brand">
          <AbcFitnessBrandLogo
            to="/"
            theme="dark"
            testId="gf-nav-logo"
          />
          <span className="portal-topnav__sep" aria-hidden>
            /
          </span>
          <GlofoxBrandLogo to="/glofox" testId="gf-nav-glofox" />
        </div>

        <ul className="nav-links portal-topnav__desktop-links">
          <li>
            <Link
              to="/glofox"
              style={{ color: navLinkColor(docsActive) }}
              aria-current={docsActive ? "page" : undefined}
              data-testid="gf-nav-docs"
            >
              Docs
            </Link>
          </li>
          <li>
            <a
              href={getGlofoxApiReferenceHref()}
              style={{ color: navLinkColor(apiReferenceActive) }}
              aria-current={apiReferenceActive ? "page" : undefined}
              data-testid="gf-nav-api-reference"
            >
              API Reference
            </a>
          </li>
          <li>
            <Link
              to={GLOFOX_REQUEST_ACCESS_HREF}
              style={{ color: navLinkColor(requestAccessActive) }}
              aria-current={requestAccessActive ? "page" : undefined}
              data-testid="gf-nav-request-access"
            >
              Request access
            </Link>
          </li>
        </ul>
      </div>

      <div className="portal-topnav__right nav-right">
        <GlofoxDocSearch />
        <div className="portal-topnav__auth portal-topnav__desktop-auth">
          {authBlock}
        </div>
      </div>

      <MobileNavDrawer
        open={menuOpen}
        onClose={closeMenu}
        footer={
          <div className="mobile-nav-drawer__auth" data-testid="gf-nav-drawer-auth">
            {displayName ? (
              <UserAccountMenu
                userName={displayName}
                testIdPrefix="gf-nav-drawer"
                variant="portal"
              />
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn btn-outline portal-topnav__auth-btn"
                  data-testid="gf-nav-drawer-login"
                  onClick={closeMenu}
                >
                  <i className="ti ti-login" aria-hidden />
                  <span>Log in</span>
                </Link>
                <Link
                  to="/signup"
                  className="btn btn-outline portal-topnav__auth-btn"
                  data-testid="gf-nav-drawer-signup"
                  onClick={closeMenu}
                >
                  <i className="ti ti-user-plus" aria-hidden />
                  <span>Sign up</span>
                </Link>
              </>
            )}
          </div>
        }
      >
        <ul className="mobile-nav-drawer__links">
          <li>
            <Link
              to="/glofox"
              className={`mobile-nav-drawer__link${docsActive ? " is-active" : ""}`}
              aria-current={docsActive ? "page" : undefined}
              data-testid="gf-nav-drawer-docs"
              onClick={closeMenu}
            >
              Docs
            </Link>
          </li>
          <li>
            <a
              href={getGlofoxApiReferenceHref()}
              className={`mobile-nav-drawer__link${apiReferenceActive ? " is-active" : ""}`}
              aria-current={apiReferenceActive ? "page" : undefined}
              data-testid="gf-nav-drawer-api-reference"
              onClick={closeMenu}
            >
              API Reference
            </a>
          </li>
          <li>
            <Link
              to={GLOFOX_REQUEST_ACCESS_HREF}
              className={`mobile-nav-drawer__link${requestAccessActive ? " is-active" : ""}`}
              aria-current={requestAccessActive ? "page" : undefined}
              data-testid="gf-nav-drawer-request-access"
              onClick={closeMenu}
            >
              Request access
            </Link>
          </li>
        </ul>

        <div className="mobile-nav-drawer__docs" data-testid="gf-nav-drawer-docs-nav">
          <GlofoxSidebarNav testIdPrefix="gf-drawer" onNavigate={closeMenu} />
        </div>
      </MobileNavDrawer>
    </nav>
    </>
  );
}
